/* eslint-disable @typescript-eslint/no-explicit-any */
import { type ComponentType, type ReactNode, useMemo } from "react";

import { type TooltipTriggerProps } from "@radix-ui/react-tooltip";

import { Tooltip } from "./Tooltip";

export type WithTooltipProps = {
  tooltip?: ReactNode | string;
  disabledTooltip?: ReactNode | string;
  disabled?: boolean;
  triggerProps?: TooltipTriggerProps;
};

// Both uses of 'any' are the necessary evil here, as typescript infer strips away generic types from the props
export function withTooltip<
  P extends object,
  R = ComponentType<P & WithTooltipProps>,
>(
  WrappedComponent: ComponentType<P>,
): R extends ComponentType<any> ? R : ComponentType<P & WithTooltipProps> {
  const WithTooltipComponent = ({
    tooltip,
    disabledTooltip,
    disabled,
    triggerProps,
    ...props
  }: P & WithTooltipProps) => {
    let tooltipContent: ReactNode | string | undefined;

    const memoizedTriggerProps = useMemo(
      (): Omit<TooltipTriggerProps, "children"> => ({
        asChild: true,
        ...(triggerProps ?? {}),
      }),
      [triggerProps],
    );

    if (disabled && disabledTooltip) {
      tooltipContent = disabledTooltip;
    } else if (tooltip) {
      tooltipContent = tooltip;
    }

    if (!tooltipContent) {
      return <WrappedComponent {...(props as P)} disabled={disabled} />;
    }

    return (
      <Tooltip content={tooltipContent} triggerProps={memoizedTriggerProps}>
        <WrappedComponent {...(props as P)} disabled={disabled} />
      </Tooltip>
    );
  };

  WithTooltipComponent.displayName = `WithTooltip(${
    WrappedComponent.displayName || WrappedComponent.name || "Component"
  })`;

  return WithTooltipComponent as any;
}
